import { useEffect, useRef } from "react";
import { t } from "../i18n";

// Minimal transient confirmation toast. Fixed to the bottom-right of the
// viewport, non-blocking, auto-dismisses after `durationMs` (~4s default).
// Used for the post-assign promotion summary (spec §3): a quiet "here's
// what was added" without gating the user. Controlled by the parent —
// render <Toast> when there's a message, null otherwise; the parent owns
// the message string + clears it on dismiss.

interface Props {
  message: string;
  onDismiss: () => void;
  durationMs?: number;
  /** Optional inline action button (e.g. "Undo"). Clicking it triggers the
   *  action AND dismisses the toast. The auto-dismiss timer is NOT reset. */
  action?: { label: string; onClick: () => void };
}

export function Toast({ message, onDismiss, durationMs = 4000, action }: Props) {
  // Hold onDismiss in a ref so an unstable inline parent callback (new identity
  // every render) doesn't restart the auto-dismiss timer on each parent
  // re-render — the timer depends only on the message + duration.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  useEffect(() => {
    const t = setTimeout(() => onDismissRef.current(), durationMs);
    return () => clearTimeout(t);
  }, [message, durationMs]);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: 20,
        insetInlineEnd: 20,
        zIndex: 1000,
        maxWidth: 360,
        padding: "10px 14px",
        background: "var(--surface)",
        color: "var(--text)",
        border: "1px solid var(--success, #6aff9c)",
        borderInlineStart: "3px solid var(--success, #6aff9c)",
        borderRadius: 6,
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.4)",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span style={{ flex: 1 }}>{message}</span>
      {action && (
        <button
          type="button"
          onClick={() => { action.onClick(); onDismiss(); }}
          style={{
            background: "none",
            border: "1px solid var(--text-muted, #9ca3af)",
            borderRadius: 4,
            cursor: "pointer",
            color: "var(--text)",
            fontSize: 12,
            padding: "2px 8px",
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t("common.dismiss")}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--text-muted, #9ca3af)",
          fontSize: 14,
          padding: 0,
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  );
}
